import React, { useState } from "react";
import { Stack } from "react-bootstrap";

import "./AllowLocation.scss";
import LocationPinIcon from "../icons/LocationPinIcon";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
  WebApiPermissionStates,
} from "../../utils/constants/generic-types";
import { CustomSpinner } from "../CustomSpinner/CustomSpinner";
import { HandleGetDeviceLocationInfo } from "../topup/TopupFlow/types";
import { useTranslation } from "react-i18next";
import useAnalytics from "../../hooks/useAnalytics";
import { AllowLocationOptionProperty } from "../../utils/analytics";
import PreciseLocationNotFound from "../PreciseLocationNotFound/PreciseLocationNotFound";
import useDisclosure from "../../hooks/useDisclosure";

interface AllowLocationProps extends GenericModalProps {
  handleDeviceLocationCheck: HandleGetDeviceLocationInfo;
  openAllowLocationManually: FunctionWithVoidReturn;
  handleSuccessfulPrelimTopupChecks?: FunctionWithVoidReturn;
}

export default function AllowLocation({
  isOpen,
  onClose,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
  openAllowLocationManually,
}: AllowLocationProps) {
  const { t } = useTranslation("common", { keyPrefix: "location" });
  const [loading, setLoading] = useState<boolean>(false);
  const { sendAllowLocationOptionsEvent } = useAnalytics();
  const {
    isOpen: isPreciseLocationNotFoundModalOpen,
    onOpen: openPreciseLocationNotFoundModal,
    onClose: closePreciseLocationNotFoundModal,
  } = useDisclosure();

  const getDeviceLocationInfo = async () => {
    setLoading(true);
    handleDeviceLocationCheck(
      ({ locationStatus, isLocationPrecise = true, canTopup }) => {
        setLoading(false);

        if (locationStatus === WebApiPermissionStates.DENIED) {
          sendAllowLocationOptionsEvent(
            AllowLocationOptionProperty.ACCESS_DENIED
          );
          onClose();
          openAllowLocationManually();
          return;
        }

        if (!isLocationPrecise) {
          onClose();
          openPreciseLocationNotFoundModal();
          return;
        }

        if (canTopup) {
          handleSuccessfulPrelimTopupChecks?.();
          onClose();
        }
        sendAllowLocationOptionsEvent(
          AllowLocationOptionProperty.ACCESS_GRANTED
        );
      }
    );
  };

  const handleCloseModal = () => {
    sendAllowLocationOptionsEvent(AllowLocationOptionProperty.MODAL_CLOSED);
    onClose();
  };

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={handleCloseModal}
        containerClass="AllowLocationModal"
      >
        <div className="pt-4 d-flex flex-column justify-content-between ModalContent">
          <Stack gap={3} className="align-items-start">
            <LocationPinIcon />
            <Stack gap={2} className="align-items-start Copy">
              <h1 className="Grotesk MainCopy">
                {t("allow_location_services")}
              </h1>
              <p className="text-start Grotesk SubCopy">
                {t("we_are_legally_required_to_ask_you_for_your_location")}
              </p>
            </Stack>
          </Stack>
          <PrimaryActionButton
            size="lg"
            text={!loading ? t("continue") : <CustomSpinner size="sm" />}
            onClick={getDeviceLocationInfo}
          />
        </div>
      </BottomDrawer>

      <PreciseLocationNotFound
        isOpen={isPreciseLocationNotFoundModalOpen}
        onClose={closePreciseLocationNotFoundModal}
        handleDeviceLocationCheck={handleDeviceLocationCheck}
        handleSuccessfulPrelimTopupChecks={handleSuccessfulPrelimTopupChecks}
        openAllowLocationManually={openAllowLocationManually}
      />
    </>
  );
}
